// Native Scribe transport. Keep audio, provider payloads, tokens and URLs out of logs.
const SAMPLE_RATE = 16000;
const STARTUP_MS = 15000;
// Preserve speech during token/connection setup; never discard older words.
const MAX_QUEUED_BYTES = SAMPLE_RATE * 2 * STARTUP_MS / 1000;
// Base64 plus JSON overhead for approximately two seconds (including short-run padding).
const MAX_SOCKET_BUFFER_BYTES = 96000;
const MIN_INITIAL_SAMPLES = Math.ceil(SAMPLE_RATE * 2.1);
const FINALIZATION_MS = 10000;
const MIN_FINALIZATION_MS = 4000;
const TRANSCRIPT_QUIET_MS = 1500;
const UPLOAD_MS = 5000;
const QUEUE_PUMP_MS = 50;
// Main and inline composers share one browser microphone owner. Taking ownership
// cancels the previous run synchronously, including any pending permission grant.
let microphoneOwner = null;
const PROVIDER_ERRORS = new Set([
  'auth_error', 'quota_exceeded', 'transcriber_error', 'input_error',
  'invalid_request', 'error', 'commit_throttled', 'unaccepted_terms',
  'rate_limited', 'queue_overflow', 'resource_exhausted',
  'session_time_limit_exceeded', 'chunk_size_exceeded', 'insufficient_audio_activity',
]);

const ERRORS = {
  no_microphone: {
    message: 'No encontramos un micrófono disponible. Comprueba que esté conectado y activado.',
  },
  permission: {
    message: 'No pudimos acceder al micrófono. Revisa los permisos del sitio y del dispositivo e inténtalo de nuevo.',
    help: 'En los ajustes de este sitio, permite el micrófono. Revisa también los permisos de micrófono del navegador en los ajustes del dispositivo. Si el dispositivo está administrado, consulta con su administrador.',
  },
  microphone_busy: {
    message: 'No pudimos usar el micrófono. Cierra otras aplicaciones que puedan estar usándolo e inténtalo de nuevo.',
  },
  unsupported: {
    message: 'El dictado necesita una conexión segura HTTPS y un navegador actualizado compatible con micrófono. Puedes seguir escribiendo.',
  },
  interrupted: {
    message: 'Se interrumpió el dictado. Revisa el texto antes de volver a dictar.',
  },
  network: {
    message: 'Falló la conexión del dictado. Revisa tu conexión e inténtalo de nuevo.',
  },
  unavailable: {
    message: 'El dictado no está disponible. Puedes seguir escribiendo e intentarlo más tarde.',
  },
  throttled: {
    message: 'Se alcanzó un límite del dictado. Inténtalo más tarde.',
  },
  authentication: {
    message: 'Inicia sesión para usar el dictado.',
  },
  invalid_origin: {
    message: 'Abre el chat desde el sitio de Lexius de tu país para usar el dictado.',
  },
  buffering: {
    message: 'El envío de audio se retrasó demasiado. Revisa el texto e inténtalo de nuevo.',
  },
  incomplete: {
    message: 'Revisa el texto de la transcripción.',
  },
};

/** Normalize browser errors without exposing device identifiers or provider details. */
export function dictationError(error) {
  let code = Object.hasOwn(ERRORS, error?.code) ? error.code : 'unavailable';
  if (['NotAllowedError', 'PermissionDeniedError', 'SecurityError'].includes(error?.name)) code = 'permission';
  if (['NotFoundError', 'DevicesNotFoundError', 'OverconstrainedError'].includes(error?.name)) code = 'no_microphone';
  if (['NotReadableError', 'TrackStartError', 'AbortError'].includes(error?.name)) code = 'microphone_busy';
  if (error?.name === 'NotSupportedError') code = 'unsupported';
  return { code, ...ERRORS[code] };
}

function stopTracks(stream) {
  for (const track of stream?.getTracks?.() || []) {
    try { track.stop(); } catch (_) { /* Already stopped. */ }
  }
}

function encodeAudio(buffer, encode) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 8192) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
  }
  return encode(binary);
}

/**
 * One microphone/session owner. start() must be called directly from a gesture.
 * Callbacks carry runId and editable; freezing revokes draft authority immediately.
 * Dependencies make races and browser failures testable without real microphones.
 */
export function createDictationController(callbacks = {}, dependencies = {}) {
  const platform = globalThis;
  const deps = {
    isSecureContext: () => platform.isSecureContext === true,
    getUserMedia: platform.navigator?.mediaDevices?.getUserMedia?.bind(platform.navigator.mediaDevices),
    holdResponseAudio: () => platform.LexiusResponseAudio?.holdForMicrophone?.(),
    createAudioContext: () => {
      const AudioContext = platform.AudioContext || platform.webkitAudioContext;
      return new AudioContext({ latencyHint: 'interactive' });
    },
    createWorkletNode: (context) => new platform.AudioWorkletNode(context, 'dictation-pcm-capture', {
      numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1],
      channelCount: 1, channelCountMode: 'explicit',
    }),
    createWebSocket: (url) => new platform.WebSocket(url),
    fetch: platform.fetch?.bind(platform),
    encodeBase64: platform.btoa?.bind(platform),
    setTimeout: platform.setTimeout.bind(platform),
    clearTimeout: platform.clearTimeout.bind(platform),
    workletUrl: '',
    ...dependencies,
  };
  let serial = 0;
  let current = null;
  let state = 'idle';
  let disposed = false;
  const ownership = { cancel };

  const valid = (run) => current === run && run.active && !disposed && microphoneOwner === ownership;
  const emitMetric = (name, reason) => callbacks.onMetric?.({ name, ...(reason ? { reason } : {}) });
  function setState(next, run, error) {
    state = next;
    callbacks.onState?.({ state, runId: run?.id ?? serial, ...(error ? { error } : {}) });
  }
  function clearTimer(run, key) {
    if (run[key] != null) deps.clearTimeout(run[key]);
    run[key] = null;
  }
  function releaseCapture(run) {
    for (const remove of run.trackListeners) remove();
    run.trackListeners = [];
    stopTracks(run.stream);
    run.stream = null;
    try { run.source?.disconnect(); } catch (_) { /* Already disconnected. */ }
    run.source = null;
    run.releaseResponseAudio?.();
    run.releaseResponseAudio = null;
  }
  function releaseProcessing(run) {
    if (run.worklet) {
      run.worklet.port.onmessage = null;
      run.worklet.onprocessorerror = null;
      try { run.worklet.disconnect(); } catch (_) { /* Already disconnected. */ }
      try { run.worklet.port.close?.(); } catch (_) { /* Already closed. */ }
      run.worklet = null;
    }
    if (run.context) {
      run.context.onstatechange = null;
      try { Promise.resolve(run.context.close()).catch(() => {}); } catch (_) { /* Already closed. */ }
      run.context = null;
    }
  }
  function cleanup(run) {
    run.active = false;
    if (microphoneOwner === ownership) microphoneOwner = null;
    for (const key of ['startupTimer', 'finalTimer', 'flushTimer', 'queueTimer',
      'uploadTimer', 'drainTimer', 'minimumTimer', 'quietTimer']) clearTimer(run, key);
    run.abort.abort();
    releaseCapture(run);
    releaseProcessing(run);
    if (run.socket) {
      run.socket.onopen = null;
      run.socket.onmessage = null;
      run.socket.onerror = null;
      run.socket.onclose = null;
      try { run.socket.close(); } catch (_) { /* Already closed. */ }
      run.socket = null;
    }
    run.queued = [];
    run.queuedBytes = 0;
    run.resolveStop?.();
    run.resolveStop = null;
  }
  function fail(run, error) {
    if (!valid(run)) return;
    const normalized = dictationError(error);
    cleanup(run);
    current = null;
    if (normalized.code === 'incomplete') emitMetric('finalization_timeout', error.reason);
    else emitMetric('connection_failure', normalized.code);
    setState('error', run, normalized);
  }
  function finish(run) {
    if (!valid(run)) return;
    cleanup(run);
    current = null;
    // VAD commits have no correlation ID or end-of-stream acknowledgment.
    // A settled transcript is ready for review, never proof of full processing.
    emitMetric('finalization_unconfirmed');
    setState('review', run);
  }
  function sendAudio(run, buffer, commit = false) {
    if (!valid(run)) return false;
    if (!run.ready || run.socket?.readyState !== 1) {
      fail(run, { code: 'network' });
      return false;
    }
    try {
      const message = JSON.stringify({
        message_type: 'input_audio_chunk', audio_base_64: encodeAudio(buffer, deps.encodeBase64),
        sample_rate: SAMPLE_RATE, ...(commit ? { commit: true } : {}),
        ...(!run.sentSamples && buffer.byteLength && run.previousText ? { previous_text: run.previousText } : {}),
      });
      if (run.socket.bufferedAmount + message.length > MAX_SOCKET_BUFFER_BYTES) {
        fail(run, { code: 'buffering' });
        return false;
      }
      run.socket.send(message);
      run.sentSamples += buffer.byteLength / 2;
      return true;
    } catch (_) {
      fail(run, { code: 'network' });
      return false;
    }
  }
  function acceptAudio(run, buffer) {
    // Context is allowed on the first audio message only. Empty capture frames
    // must not consume that position (the explicit final commit is separate).
    if (!buffer.byteLength) return;
    if (!run.ready || run.queued.length || run.queueTimer != null) {
      if (run.queuedBytes + buffer.byteLength > MAX_QUEUED_BYTES) {
        fail(run, { code: 'buffering' });
        return;
      }
      run.queued.push(buffer);
      run.queuedBytes += buffer.byteLength;
      if (run.ready && run.queueTimer == null) drainQueuedAudio(run);
    } else {
      sendAudio(run, buffer);
    }
  }
  function drainQueuedAudio(run) {
    if (!valid(run) || !run.ready) return;
    clearTimer(run, 'queueTimer');
    // A live-capable uplink may need slower catch-up to avoid socket overflow.
    if (run.queued.length && run.socket.bufferedAmount <= MAX_SOCKET_BUFFER_BYTES / 2) {
      const buffer = run.queued.shift();
      run.queuedBytes -= buffer.byteLength;
      if (!sendAudio(run, buffer)) return;
    }
    // Catch up at up to four times realtime instead of bursting a startup
    // recording into WebSocket/provider buffers. New audio remains in order.
    if (run.queued.length) {
      run.queueTimer = deps.setTimeout(() => drainQueuedAudio(run), QUEUE_PUMP_MS);
    } else if (run.flushed) {
      commitAfterFlush(run);
    }
  }
  function canReview(run) {
    // A user may stop after VAD already committed the last spoken segment.
    // An empty stop response must neither erase nor certify a pending partial.
    return run.hasCommittedText && !run.pendingPartial;
  }
  function finishIfSettled(run) {
    if (valid(run) && run.minimumWaitElapsed && run.quietElapsed && canReview(run)) finish(run);
  }
  function noteTranscript(run) {
    if (!run.commitSent) return;
    run.quietElapsed = false;
    clearTimer(run, 'quietTimer');
    run.quietTimer = deps.setTimeout(() => {
      run.quietElapsed = true;
      finishIfSettled(run);
    }, TRANSCRIPT_QUIET_MS);
  }
  function waitForUpload(run) {
    if (!valid(run)) return;
    clearTimer(run, 'drainTimer');
    if (run.socket.bufferedAmount > 0) {
      run.drainTimer = deps.setTimeout(() => waitForUpload(run), QUEUE_PUMP_MS);
      return;
    }
    clearTimer(run, 'uploadTimer');
    // Give the provider its response window only after the final packet leaves
    // the browser buffer. Upload and worklet flushing have separate bounds.
    noteTranscript(run);
    run.minimumTimer = deps.setTimeout(() => {
      run.minimumWaitElapsed = true;
      finishIfSettled(run);
    }, MIN_FINALIZATION_MS);
    run.finalTimer = deps.setTimeout(() => {
      if (!valid(run)) return;
      if (canReview(run)) finish(run);
      else fail(run, { code: 'incomplete' });
    }, FINALIZATION_MS);
  }
  function commitAfterFlush(run) {
    if (!valid(run) || run.commitSent) return;
    // Scribe starts processing after its initial two-second audio window. Silence
    // preserves very short recordings without continuing to own the microphone.
    while (run.sentSamples < MIN_INITIAL_SAMPLES) {
      const count = Math.min(3200, MIN_INITIAL_SAMPLES - run.sentSamples);
      if (!sendAudio(run, new ArrayBuffer(count * 2))) return;
    }
    run.commitSent = true;
    if (sendAudio(run, new ArrayBuffer(0), true)) waitForUpload(run);
  }
  function handleMessage(run, event) {
    if (!valid(run)) return;
    let data;
    try { data = JSON.parse(event.data); } catch (_) {
      fail(run, { code: 'unavailable' });
      return;
    }
    if (!data || typeof data !== 'object') {
      fail(run, { code: 'unavailable' });
      return;
    }
    if (data.message_type === 'session_started') {
      if (run.ready) return;
      if (run.context?.state !== 'running') {
        fail(run, { code: 'interrupted' });
        return;
      }
      run.ready = true;
      clearTimer(run, 'startupTimer');
      drainQueuedAudio(run);
      if (!valid(run)) return;
      setState('listening', run);
      emitMetric('listening');
    } else if (data.message_type === 'partial_transcript' && typeof data.text === 'string') {
      run.pendingPartial = Boolean(data.text.trim());
      noteTranscript(run);
      callbacks.onPartial?.({ text: data.text, runId: run.id, editable: run.editable });
    } else if (data.message_type === 'committed_transcript' && typeof data.text === 'string') {
      noteTranscript(run);
      // An empty commit is not authoritative wording for a pending partial.
      if (data.text.trim()) {
        run.hasCommittedText = true;
        run.pendingPartial = false;
        callbacks.onCommitted?.({ text: data.text, runId: run.id, editable: run.editable });
      }
    } else if (PROVIDER_ERRORS.has(data.message_type)) {
      fail(run, { code: ['rate_limited', 'commit_throttled'].includes(data.message_type) ? 'throttled' : 'unavailable' });
    }
  }
  async function prepare(run, microphonePromise, resumePromise) {
    try {
      const stream = await microphonePromise;
      if (!valid(run)) { stopTracks(stream); return; }
      run.stream = stream;
      const tracks = stream.getAudioTracks();
      if (!tracks.length || tracks.every(track => track.readyState === 'ended')) {
        fail(run, { code: 'no_microphone' });
        return;
      }
      for (const track of tracks) {
        for (const event of ['ended', 'mute']) {
          const listener = () => fail(run, { code: 'interrupted' });
          track.addEventListener(event, listener);
          run.trackListeners.push(() => track.removeEventListener(event, listener));
        }
      }
      setState('connecting', run);
      run.startupTimer = deps.setTimeout(() => fail(run, { code: 'unavailable' }), STARTUP_MS);
      await resumePromise;
      if (!valid(run)) return;
      await run.context.audioWorklet.addModule(deps.workletUrl);
      if (!valid(run)) return;
      if (run.context.state !== 'running') {
        fail(run, { code: 'interrupted' });
        return;
      }
      run.source = run.context.createMediaStreamSource(stream);
      run.worklet = deps.createWorkletNode(run.context);
      run.worklet.onprocessorerror = () => fail(run, { code: 'interrupted' });
      run.worklet.port.onmessage = ({ data }) => {
        if (!valid(run)) return;
        if (data?.type === 'audio' && data.buffer instanceof ArrayBuffer && !run.commitSent) {
          acceptAudio(run, data.buffer);
        } else if (data?.type === 'flushed' && data.id === run.flushId) {
          if (run.flushed) return;
          run.flushed = true;
          clearTimer(run, 'flushTimer');
          releaseProcessing(run);
          // Allow queued PCM to drain at realtime speed, plus the socket upload bound.
          run.uploadTimer = deps.setTimeout(
            () => fail(run, { code: 'incomplete', reason: 'audio_upload' }),
            UPLOAD_MS + Math.ceil(run.queuedBytes * 1000 / (SAMPLE_RATE * 2)),
          );
          if (!run.queued.length) commitAfterFlush(run);
        }
      };
      run.context.onstatechange = () => {
        if (valid(run) && !run.stopping && run.context.state !== 'running') fail(run, { code: 'interrupted' });
      };
      // Capture before network setup so the microphone indicator cannot hide
      // a gap in the opening words. Cancellation still clears all queued audio.
      run.source.connect(run.worklet);
      run.worklet.connect(run.context.destination);
      // A token is never requested before successful microphone and audio setup.
      const response = await deps.fetch('/chat/dictation/token', {
        method: 'POST', credentials: 'same-origin', cache: 'no-store', redirect: 'error',
        headers: { 'Content-Type': 'application/json' }, body: '{}', signal: run.abort.signal,
      });
      if (!valid(run)) return;
      if (!response.ok) {
        const code = { 401: 'authentication', 403: 'invalid_origin', 429: 'throttled' }[response.status] || 'unavailable';
        fail(run, { code });
        return;
      }
      const payload = await response.json();
      if (!valid(run)) return;
      if (typeof payload?.token !== 'string' || !payload.token.trim() || payload.token.length > 4096) {
        fail(run, { code: 'unavailable' });
        return;
      }
      const query = new URLSearchParams({
        model_id: 'scribe_v2_realtime', language_code: 'es', audio_format: 'pcm_16000',
        commit_strategy: 'vad', include_timestamps: 'false', token: payload.token,
      });
      for (const term of run.keyterms) query.append('keyterms', term);
      run.socket = deps.createWebSocket(`wss://api.elevenlabs.io/v1/speech-to-text/realtime?${query}`);
      run.socket.onmessage = event => handleMessage(run, event);
      run.socket.onerror = () => fail(run, { code: 'network' });
      run.socket.onclose = () => fail(run, { code: 'network' });
    } catch (error) {
      fail(run, error);
    }
  }

  function start({ previousText = '', keyterms = [] } = {}) {
    if (disposed || current) return null;
    const run = {
      id: ++serial, active: true, editable: true, abort: new AbortController(),
      trackListeners: [], queued: [], queuedBytes: 0, sentSamples: 0,
      ready: false, stopping: false, commitSent: false, hasCommittedText: false,
      pendingPartial: false, minimumWaitElapsed: false, quietElapsed: false,
      previousText: typeof previousText === 'string' ? Array.from(previousText.replace(/\s+/g, ' ').trim()).slice(-49).join('') : '',
      keyterms: [],
    };
    const seenTerms = new Set();
    for (const value of Array.isArray(keyterms) ? keyterms : []) {
      if (typeof value !== 'string') continue;
      const term = value.trim();
      const key = term.toLowerCase();
      if (!term || Array.from(term).length > 20 || seenTerms.has(key)) continue;
      seenTerms.add(key);
      run.keyterms.push(term);
      if (run.keyterms.length === 50) break;
    }
    current = run;
    const previousOwner = microphoneOwner;
    microphoneOwner = ownership;
    previousOwner?.cancel();
    // Cancelling the previous composer can synchronously notify UI listeners.
    if (!valid(run)) return run.id;
    setState('requesting_permission', run);
    if (!valid(run)) return run.id;
    try {
      if (!deps.isSecureContext() || !deps.getUserMedia || !deps.fetch || !deps.encodeBase64) {
        fail(run, { code: 'unsupported' });
        return run.id;
      }
      // Release iOS's playback-only category before opening either microphone
      // composer, and prevent response playback from reclaiming it mid-capture.
      run.releaseResponseAudio = deps.holdResponseAudio();
      if (!valid(run)) {
        run.releaseResponseAudio?.();
        run.releaseResponseAudio = null;
        return run.id;
      }
      // Both calls stay in the originating button gesture, before the first await.
      run.context = deps.createAudioContext();
      if (!run.context?.audioWorklet || !deps.workletUrl) {
        fail(run, { code: 'unsupported' });
        return run.id;
      }
      const resumePromise = Promise.resolve(run.context.resume()).catch(error => {
        fail(run, error);
      });
      const microphonePromise = deps.getUserMedia({
        audio: { channelCount: { ideal: 1 }, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: false,
      });
      void prepare(run, microphonePromise, resumePromise);
    } catch (error) {
      fail(run, error?.name === 'TypeError' ? { code: 'unsupported' } : error);
    }
    return run.id;
  }
  function cancel() {
    const run = current;
    if (!run) {
      if (microphoneOwner === ownership) microphoneOwner = null;
      if (state !== 'idle' && !disposed) setState('idle');
      return;
    }
    cleanup(run);
    current = null;
    setState('idle', run);
  }
  function stop({ editable = true } = {}) {
    const run = current;
    if (!run) return Promise.resolve();
    if (!editable) run.editable = false;
    if (run.stopping) return run.stopPromise;
    if (!run.ready) { cancel(); return Promise.resolve(); }
    run.stopping = true;
    run.stopPromise = new Promise(resolve => { run.resolveStop = resolve; });
    releaseCapture(run);
    setState('finalizing', run);
    run.flushId = `${run.id}:final`;
    run.flushTimer = deps.setTimeout(() => {
      emitMetric('finalization_timeout', 'audio_flush');
      fail(run, { code: 'interrupted' });
    }, 500);
    try { run.worklet.port.postMessage({ type: 'flush', id: run.flushId }); } catch (_) {
      fail(run, { code: 'interrupted' });
    }
    return run.stopPromise;
  }
  return {
    start, stop, cancel,
    freezeForEdit() { return stop({ editable: false }); },
    dispose() { cancel(); disposed = true; },
    getState() { return state; },
    isBusy() { return Boolean(current); },
  };
}
